import { CommonDialogs } from "@mcleod/common";
import {
    Button, ButtonProps, ButtonVariant, ClickEvent, Panel, PanelProps, Snackbar, Table, TableRow, TableSelectionEvent,
    Toast
} from "@mcleod/components";
import { ImageName } from "@mcleod/images";
import { Api, ModelRow } from "@mcleod/core";
import { MobileCommList } from "./MobileCommList";
import { MobileCommSendMessage } from "./MobileCommSendMessage";
import { MobileCommListTable } from "./MobileCommListTable";

interface ActionButtonProps {
    button: Button;
    /**
     * If the shouldEnable() function is provided, it will be called when the table selection changes.
     * The button will be enabled or disabled based off the return value of this function.
     * If shouldEnable() is not provided, the button will always be enabled when a row is selected.
     * @param modelRow
     */
    shouldEnable?: (modelRow:ModelRow) => boolean;
}

const buttonProps: Partial<ButtonProps> = {
    color: "primary", variant: ButtonVariant.round, borderWidth: 0,
    imageWidth: 22, imageHeight: 22, rowBreak: false, enabled: false
}

const borderPanelProps: Partial<PanelProps> = {
    height: 20,
    padding: 0,
    marginRight: 10,
    borderRightWidth: 1,
    borderRightColor: "subtle.light",
    fillRow: true,
    rowBreak: false
}

/**
 * There are 3 steps to adding a new action button:
 * 1. Add a new ActionButtonType enum value.
 * 2. Create a new function that will return the button.
 * 3. Using the new enum value and the function from step 2, create a new ActionButtonProps object and add it to the
 *   buttonProps map in the buildButtonMap() function.  See the comment in ActionButtonProps interface for more information.
 */

export enum ActionButtonType {
    ADD,
    REPLY,
    CONFIRM,
    FLEET
}

export class MobileCommListToolPanel extends Panel {
    private layout: MobileCommListTable;
    private table: Table;
    private buttonMap: Map<ActionButtonType,ActionButtonProps>;
    private buttonTypes: ActionButtonType[];

    constructor(layout:MobileCommListTable, ...buttonsTypes:ActionButtonType[]) {
        super({ marginLeft: 15, paddingTop: 0, paddingBottom: 0 });

        this.layout = layout;
        this.table = layout.tableMcMessage;
        this.buttonTypes = buttonsTypes;
        this.configureToolPanel();
        if (buttonsTypes.length > 0) {
            this.buildButtonMap();
            this.addActionButtons();
            this.layout.tableMcMessage.addSelectionListener((event: TableSelectionEvent) => this.onSelectionChanged(event));
        }
        this.table.addCenterTool(this);
    }

    private configureToolPanel() {
        this.table.overrideUrlParams = (selectedRow:TableRow) => {
            return { orderId: selectedRow.data.get("order_id"), movementId: selectedRow.data.get("movement_id") }
        };
    }

    /**
     * When adding a new action button, add it to the buttonProps map here.
     * @private
     */
    private buildButtonMap() {
        this.buttonMap = new Map();
        this.buttonMap.set(ActionButtonType.ADD, {button: this.getAddButton(), shouldEnable(modelRow) {
            return true;
        }});
        this.buttonMap.set(ActionButtonType.REPLY, {button: this.getReplyButton(), shouldEnable(modelRow) {
            return "M" === modelRow.get("row_type");
        }});
        this.buttonMap.set(ActionButtonType.CONFIRM, {button: this.getConfirmButton(), shouldEnable(modelRow) {
            return "M" === modelRow.get("row_type");
        }});
        this.buttonMap.set(ActionButtonType.FLEET, {button: this.getFleetButton(), shouldEnable(modelRow) {
            return "M" === modelRow.get("row_type");
        }});
    }

    /**
     * This adds the action buttons to the panel.
     */
    async addActionButtons() {
        this.add(new Panel({ ...borderPanelProps, marginLeft: 0, marginRight: 10 }));
        this.buttonTypes.forEach(buttonType => this.add(this.buttonMap.get(buttonType).button));
        this.add(new Panel({ ...borderPanelProps, marginLeft: 10, marginRight: 0 }));
        if (this.components.length == 2) {
            this.removeAll();
        }
    }

    private onSelectionChanged(event:TableSelectionEvent) {
        const modelRow = event.newRow?.data as ModelRow;
        this.buttonTypes.forEach(buttonType => {
            const props = this.buttonMap.get(buttonType);
            if (modelRow == null && buttonType != ActionButtonType.ADD) {
                props.button.enabled = false;
            } else if (props.shouldEnable != null) {
                props.button.enabled = props.shouldEnable(modelRow);
            } else {
                props.button.enabled = true;
            }
        })
    }

    public getButton(buttonType:ActionButtonType): Button {
        return this.buttonMap.get(buttonType).button;
    }

    // START - Action button functions
    private getAddButton(): Button {
        const buttonAdd = new Button({
            ...buttonProps,
            id: "buttonAdd",
            imageName: ImageName.add,
            imageHeight: 17,
            imageWidth: 17,
            tooltip: "Add",
            enabled:true,
        });
       buttonAdd.addClickListener((event: ClickEvent) => this.addOnClick(event));
       return buttonAdd;
    }

    private addOnClick(event: ClickEvent) {

        const button = event.target as Button;
        button.busy = true;
        this.loadSendMessageSlideout(false,null);
        button.busy = false;
    }

    private getReplyButton(): Button {
        const buttonReply = new Button({
            ...buttonProps,
            id: "buttonReply",
            imageName: ImageName.messagesReply,
            tooltip: "Reply"
        });
        buttonReply.addClickListener((event: ClickEvent) => this.replyOnClick(event));
        return buttonReply;
    }

    private replyOnClick(event: ClickEvent) {
        const modelRow = this.table?.selectedRow?.data;
        if (modelRow == null)
            return;

        const button = event.target as Button;
        button.busy = true;
        this.loadSendMessageSlideout(true,modelRow);
        button.busy = false;
    }

    private loadSendMessageSlideout(reply:boolean,modelRow:ModelRow){
        MobileCommSendMessage.showSlideout(modelRow,reply);
    }

    private getConfirmButton(): Button {
        const buttonConfirm = new Button({
            ...buttonProps,
            id: "buttonConfirm",
            imageName: ImageName.messagesConfirm,
            tooltip: "Confirm"
        });
        buttonConfirm.addClickListener((event: ClickEvent) => this.confirmOnClick(event));
        return buttonConfirm;
    }

    private async confirmOnClick(event: ClickEvent) {
        const modelRow = this.table?.selectedRow?.data;
        if (modelRow == null)
            return;

        const button = event.target as Button;
        button.busy = true;
        try {
            const messageId = modelRow.get("id");
            const response = await Api.post("lme/dispatch/mobilecomm/confirm-message", {
                mc_message_id: messageId
            });
            if (response?.data?.[0]?.message_confirmed) {
                Toast.showSuccessToast("Message has been confirmed.", null, null, {millisUntilDismissal: 5000});
                (this.layout.owner as MobileCommList).searchList();
            } else {
                Snackbar.showSnackbar("Failed to confirm message.");
            }
        } catch (error) {
            Snackbar.showSnackbar("Failed to confirm message.");
        }
        button.busy = false;
    }

    private getFleetButton(): Button {
        const buttonFleet = new Button({
            ...buttonProps,
            id: "buttonFleet",
            imageName: ImageName.messagesFleet,
            tooltip: "Fleet"
        });
        buttonFleet.addClickListener((event: ClickEvent) => this.fleetOnClick(event));
        return buttonFleet;
    }

    private fleetOnClick(event: ClickEvent) {
        const modelRow = this.table?.selectedRow?.data;
        if (modelRow == null)
            return;

        const button = event.target as Button;
        button.busy = true;
        CommonDialogs.showDialog("This button is a placeholder.  The functionality will be added in a later card.");
        button.busy = false;
    }
    // END - Action button functions
}
